import {Injectable, OnDestroy} from '@angular/core';

import {EMPTY, of, Subject} from 'rxjs';
import {catchError, exhaustMap, filter, map, switchMap, takeUntil, withLatestFrom} from 'rxjs/operators';

import {Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';

import {AppState} from '../../NGRX/core.state';
import {selectConfiguration} from '../../NGRX/configuration/configuration.selectors';

import {
  callCoreSettings,
  callFxRates,
  failConfiguration,
  setDisplayState,
  successCoreSettings,
  successFxRates
} from './configuration.actions';

import {Config} from "../../services/config/config.service";
import {FxRatesClient} from "../../client/fx_rates";
import {SettingsClient} from "../../client/settings";

import {setWebsocketEvent} from '../coreWebsockets/coreWebsockets.actions';
import {ws} from './configuration.reducer';


@Injectable()
export class ConfigurationEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();


  callCoreSettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(callCoreSettings),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        return this._settingsClientCore.get().pipe(
          map(data => {
            return successCoreSettings({response: data});
          }),
          catchError(error => {
            return of(failConfiguration({error: error}))
          })
        )
      }))
  }, {dispatch: true});

  callFxRates = createEffect(() => {
    return this.actions$.pipe(
      ofType(callFxRates),
      // ASK
      filter(intent => this._config.CONFIG.enableSyndicatesLeaderboard),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        return this._fxRatesClient.get().pipe(
          map(data => {
            return successFxRates({response: data});
          }),
          catchError(error => {
            return of(failConfiguration({error: error}))
          })
        )
      }))
  }, {dispatch: true});

  successCoreSettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(successCoreSettings),
      withLatestFrom(this.store.select(selectConfiguration)),
      takeUntil(this.destroy$),
      exhaustMap(([, configurationState]) => {
        // Pre-existing assumption: localSettings/micrositeType are populated whenever core settings succeed — not independently verified
        const micrositeType = configurationState.localSettings!.micrositeType!;
        return of(setDisplayState({
          displayState: {
            microsite_type: micrositeType,
            view: micrositeType.length > 1 ? 'ALL' : micrositeType[0] as string
          }
        }))
      }))
  }, {dispatch: true});

  ///// LIVE UPDATE
  setWebsocketEvent = createEffect(() => {
    return this.actions$.pipe(
      ofType(setWebsocketEvent),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
        if (ws.RELOAD_EVENTS.includes(propVal.event)) {
          return [
            callFxRates()
          ];
        }

        return EMPTY
      })
    )
  }, {dispatch: true})


  constructor(
    private actions$: Actions,
    private _settingsClientCore: SettingsClient,
    private _fxRatesClient: FxRatesClient,
    private _config: Config,
    private store: Store<AppState>
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}


